import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Sparkles, Lock } from "lucide-react";
import axios from "axios";
import { SECTORS } from "@/components/SectorsMegaMenu";
import BookDemoModal from "@/components/BookDemoModal";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Map each sector to relevant worker slugs (heuristic, catalog-driven). */
const SECTOR_WORKER_SLUGS = {
    "hr":                 ["marcus-recruiter", "fatima-hr", "elena-exec"],
    "real-estate":        ["aria-content", "marcus-recruiter", "hiro-ae"],
    "consulting":         ["elena-exec", "aria-content", "sofia-data"],
    "journalism":         ["aria-content", "sofia-data", "luna-social"],
    "students":           ["aria-content", "sofia-data", "kenji-support"],
    "education":          ["aria-content", "elena-exec", "kenji-support"],
    "restoration":        ["kenji-support", "yara-procurement", "elena-exec"],
    "startup":            ["marcus-recruiter", "aria-content", "sofia-data", "kai-engineer"],
    "sales":              ["diego-sales", "zara-sdr", "hiro-ae"],
    "health":             ["sofia-data", "chen-compliance", "kenji-support"],
    "legal":              ["ivan-legal", "chen-compliance", "elena-exec"],
    "government-affairs": ["ivan-legal", "chen-compliance", "aria-content"],
    "healthcare-admin":   ["chen-compliance", "sofia-data", "kenji-support"],
};

export default function SectorPage() {
    const { slug } = useParams();
    const [workers, setWorkers] = useState([]);
    const [demoOpen, setDemoOpen] = useState(false);
    const sector = SECTORS.find((s) => s.slug === slug);

    useEffect(() => {
        axios.get(`${API_BASE}/workers/catalog`).then((r) => {
            const wanted = new Set(SECTOR_WORKER_SLUGS[slug] || []);
            setWorkers((r.data.workers || []).filter((w) => wanted.has(w.slug)));
        }).catch(() => {});
    }, [slug]);

    if (!sector) {
        return (
            <div className="min-h-screen bg-[#fafaf7] grid place-items-center p-8">
                <div className="text-center">
                    <div className="text-lg font-medium text-gray-900">Sector not found.</div>
                    <Link to="/" className="mt-3 inline-block text-sm text-[#16a34a]">Back to home</Link>
                </div>
            </div>
        );
    }
    const Icon = sector.Icon;

    return (
        <div className="min-h-screen bg-white">
            <header className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between border-b border-[#e8e8e3]">
                <Link to="/" className="inline-flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-[#16a34a] grid place-items-center text-white text-[11px] font-semibold">W</div>
                    <span className="text-[15px] font-semibold tracking-tight text-gray-900">Nexus One</span>
                </Link>
                <nav className="flex items-center gap-4 text-sm">
                    <Link to="/pricing" className="text-gray-600 hover:text-gray-900">Pricing</Link>
                    <Link to="/login" className="text-gray-600 hover:text-gray-900">Sign in</Link>
                    <Link to="/signup" className="rounded-lg bg-[#111827] px-3.5 py-2 text-sm font-medium text-white hover:bg-black">Start free</Link>
                </nav>
            </header>

            <section className="max-w-5xl mx-auto px-6 py-20">
                <div className="w-14 h-14 rounded-xl bg-[#dcfce7] grid place-items-center mb-6">
                    <Icon className="w-7 h-7 text-[#16a34a]" strokeWidth={1.75} />
                </div>
                <div className="wfos-label text-gray-500">Sector</div>
                <h1
                    className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-[1.05]"
                    data-testid="sector-h1"
                >
                    AI workforce for {sector.label}.
                </h1>
                <p className="mt-4 text-lg text-gray-500 max-w-2xl leading-relaxed">
                    A team of specialists trained for the shape of {sector.label} work — living in the tools your organization already uses. Full vertical rollout arrives with Phase 4 & 5; join the waitlist to be first.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                    <Link
                        to="/signup"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#16a34a] px-4 h-11 text-sm font-medium text-white hover:bg-[#15803d]"
                        data-testid="sector-cta-start"
                    >
                        Get started <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                    <button
                        onClick={() => setDemoOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#e8e8e3] bg-white px-4 h-11 text-sm font-medium text-gray-900 hover:border-[#d6d6cf]"
                        data-testid="sector-cta-demo"
                    >
                        Book a demo
                    </button>
                </div>
            </section>

            {workers.length > 0 && (
                <section className="max-w-5xl mx-auto px-6 pb-20">
                    <div className="wfos-label text-gray-500 mb-3">Specialists for this sector</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sector-workers">
                        {workers.map((w) => (
                            <div key={w.slug} data-testid={`sector-worker-${w.slug}`} className="wfos-card p-5">
                                <div className="flex items-start gap-3">
                                    <img
                                        src={w.avatar_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${w.avatar_url}` : w.avatar_url}
                                        alt={w.name}
                                        className="w-12 h-12 rounded-full object-cover border border-[#e8e8e3]"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="text-[14px] font-semibold text-gray-900 truncate">{w.name}</div>
                                            {w.is_functional ? (
                                                <span className="wfos-pill wfos-pill--green text-[9px]"><Sparkles className="w-2.5 h-2.5" strokeWidth={2} /> Activated</span>
                                            ) : (
                                                <span className="wfos-pill text-[9px]"><Lock className="w-2.5 h-2.5" strokeWidth={2} /> Available</span>
                                            )}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-0.5">{w.role}</div>
                                    </div>
                                </div>
                                <p className="mt-3 text-[13px] text-gray-600 leading-relaxed line-clamp-3">{w.tagline}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="border-t border-[#e8e8e3] bg-[#fafaf7]">
                <div className="max-w-4xl mx-auto px-6 py-16 text-center">
                    <div className="wfos-label text-gray-500">Coming soon</div>
                    <h2 className="mt-2 text-2xl font-semibold text-gray-900">Full {sector.label} rollout in a later phase.</h2>
                    <p className="mt-2 text-sm text-gray-500 max-w-lg mx-auto">
                        Custom skill packs, sector-specific policy engines, and specialized Workforce Member personas.
                    </p>
                    <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                        <button
                            onClick={() => setDemoOpen(true)}
                            data-testid="sector-waitlist-cta"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#16a34a] px-4 h-11 text-sm font-medium text-white hover:bg-[#15803d]"
                        >
                            Join the waitlist <ArrowRight className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <Link
                            to="/#compare"
                            data-testid="sector-compare-link"
                            className="inline-flex items-center gap-1.5 rounded-lg atlas-btn-ghost px-4 h-11 text-[13px] font-medium"
                        >
                            See Workforce Members vs Chat Windows
                        </Link>
                    </div>
                </div>
            </section>

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} defaultInterest={sector.label} />
        </div>
    );
}
